import Child, { GenderKey } from '@/types/Child'
import Caregiver from '@/types/Caregiver'
import Relationship from '@/types/Relationship'
import firebase from './firebase'
import moment from 'moment'
import { findRelationshipByKey } from '@/constants/relationships'
import childColors from '@/constants/childColors'

type Weaken<T, K extends keyof T> = {
  [P in keyof T]: P extends K ? any : T[P]
}

interface ChildDataOut {
  birthday: firebase.firestore.Timestamp
  caregivers: any
  color: string
  createdAt: firebase.firestore.FieldValue
  genderKey: GenderKey
  name: string
}

interface ChildDataIn extends Weaken<ChildDataOut, 'createdAt'> {
  createdAt: firebase.firestore.Timestamp | null
}

type CaregiverData = {
  relationshipKey: string
  createdAt: firebase.firestore.Timestamp
  isActive: boolean
}

export default class {
  getUserId(): string {
    const currentUser = firebase.auth().currentUser
    if (!currentUser) throw new Error('required auth')
    return currentUser.uid
  }

  newChild(): Child {
    const color: string = childColors[~~(Math.random() * childColors.length)]
    return {
      birthday: moment(),
      caregivers: [],
      color,
      createdAt: moment(),
      genderKey: 'boy',
      id: '',
      name: '',
      relationshipKey: 'mother',
    }
  }

  getChildrenRealtime(callback: (children: Child[]) => any) {
    const usetId = this.getUserId()
    firebase.firestore().collection('children').where(`caregivers.${usetId}.isActive`, '==', true).onSnapshot((docs) => {
      const children: Child[] = []
      docs.forEach(doc => {
        const child = this.docToChild(doc)
        if (child) children.push(child)
      })
      callback(children)
    })
  }

  docToChild(doc: firebase.firestore.DocumentData): Child | null {
    const data: ChildDataIn = doc.data()
    if (!data.createdAt) return null
    const myUserId = this.getUserId()
    const caregivers: Caregiver[] = Object.keys(data.caregivers).map(
      (userId: string): Caregiver => {
        const caregiver: CaregiverData = data.caregivers[userId]
        return {
          userId,
          relationship: findRelationshipByKey(caregiver.relationshipKey),
          createdAt: moment(caregiver.createdAt.toDate()),
          isMe: userId === myUserId,
        }
      }
    )
    const relationshipKey = caregivers.find(o => o.isMe)?.relationship.key || 'other'
    return {
      id: doc.id,
      birthday: moment(data.birthday.toDate()),
      color: data.color,
      genderKey: data.genderKey,
      name: data.name,
      createdAt: moment(data.createdAt),
      relationshipKey,
      caregivers,
    }
  }

  getChildReference(childId: string) {
    return firebase.firestore().doc(`children/${childId}`)
  }

  validateChild(child: Child) {
    return /^.+$/.test(child.name)
  }

  async updateChild(child: Child) {
    if (!this.validateChild(child)) throw new Error()
    const userId = this.getUserId()
    const caregiver: Partial<CaregiverData> = {
      relationshipKey: child.relationshipKey,
    }
    const childData: Partial<ChildDataOut> = {
      birthday: firebase.firestore.Timestamp.fromDate(child.birthday.toDate()),
      genderKey: child.genderKey,
      color: child.color,
      name: child.name,
      caregivers: {
        [userId]: caregiver
      },
    }
    await this.getChildReference(child.id).set(childData, {merge: true})
    return ''
  }

  async createChild(child: Child): Promise<string> {
    if (!this.validateChild(child)) throw new Error()
    const userId = this.getUserId()
    const caregiver: CaregiverData = {
      relationshipKey: child.relationshipKey,
      createdAt: firebase.firestore.Timestamp.fromDate(moment().toDate()),
      isActive: true,
    }
    const data: ChildDataOut = {
      birthday: firebase.firestore.Timestamp.fromDate(child.birthday.toDate()),
      color: child.color,
      genderKey: child.genderKey,
      name: child.name,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      caregivers: {
        [userId]: caregiver,
      },
    }
    const doc = await firebase.firestore().collection('children').add(data)
    return doc.id
  }

  addCaregiver(childId: string, relationship: Relationship): Promise<void> {
    const userId = this.getUserId()
    const caregiver: CaregiverData = {
      relationshipKey: relationship.key,
      createdAt: firebase.firestore.Timestamp.fromDate(moment().toDate()),
      isActive: true,
    }
    const partial = {
      caregivers: {
        [userId]: caregiver
      },
    }
    return firebase.firestore().doc(`children/${childId}`).set(partial, {merge: true})
  }
}
